/**
 * 双方向同期の判断ロジック。
 *
 * ネットワークも IndexedDB も触らない純粋関数にしてある。
 * 同期で怖いのは「どちらを勝たせるか」の判断ミスでセーブが消えることなので、
 * その部分だけを切り出してテストで固めておく。
 */

export type SyncItemKind = 'sram' | 'state';

/**
 * Azure / fetch が返す ETag の表記ゆれを揃える（比較用・引用符なし）。
 * `"abc"` / `W/"abc"` / `abc` を同一視しないと、アップロード直後に偽競合になる。
 */
export function normalizeEtag(etag: string | undefined | null): string | undefined {
  if (etag == null) return undefined;
  let s = etag.trim();
  if (!s) return undefined;
  if (s.startsWith('W/')) s = s.slice(2).trim();
  if (s.startsWith('"') && s.endsWith('"') && s.length >= 2) {
    s = s.slice(1, -1);
  }
  return s || undefined;
}

/** If-Match / 保存用。常にダブルクォート付き。 */
export function canonicalEtag(etag: string | undefined | null): string | undefined {
  const n = normalizeEtag(etag);
  return n ? `"${n}"` : undefined;
}

/** 同期の単位。ROM の SHA-1 と種別（+ スロット）で一意になる。 */
export interface SyncKey {
  sha1: string;
  kind: SyncItemKind;
  /** kind === 'state' のときのスロット番号 */
  slot?: number;
}

export interface LocalItem extends SyncKey {
  romId: string;
  updatedAt: number;
  /** 前回同期時にサーバが返した ETag。未同期なら undefined。 */
  etag?: string | undefined;
  /** ローカルで変更され、まだ送っていない */
  dirty: boolean;
}

export interface RemoteItem extends SyncKey {
  etag: string;
  updatedAt: number;
  size: number;
  /** list API が blob メタから拾ったコア ID（任意） */
  backendId?: string;
}

export type SyncAction =
  /** ローカルにしかない、またはローカルが正当に新しい → 送る */
  | { type: 'upload'; local: LocalItem; expectedEtag?: string | undefined }
  /** リモートにしかない、またはリモートが新しい → 取り込む */
  | { type: 'download'; remote: RemoteItem; localRomId: string }
  /** 双方が別々に変更された → 触らずに保留し、UI へ判断を委ねる */
  | { type: 'conflict'; local: LocalItem; remote: RemoteItem }
  /** 対応する ROM を持っていないので取り込めない */
  | { type: 'skip'; remote: RemoteItem; reason: 'rom-not-found' }
  /** 既に一致している */
  | { type: 'in-sync'; key: SyncKey };

export function keyOf(k: SyncKey): string {
  return k.kind === 'sram' ? `${k.sha1}/sram` : `${k.sha1}/state-${k.slot ?? 0}`;
}

export interface PlanSyncOptions {
  /**
   * ユーザーが未解決のまま持っている競合キー（keyOf 形式）。
   * これらは再検出・再ダウンロードせず、解決 UI に委ねたままにする。
   */
  holdKeys?: ReadonlySet<string>;
}

/**
 * ローカルとリモートの一覧を突き合わせ、実行すべき操作を決める。
 *
 * @param romIdBySha1 手元にある ROM の SHA-1 → romId。
 *                    持っていない ROM のセーブは取り込んでも使えないので skip する。
 */
export function planSync(
  local: LocalItem[],
  remote: RemoteItem[],
  romIdBySha1: ReadonlyMap<string, string>,
  options?: PlanSyncOptions,
): SyncAction[] {
  const remoteByKey = new Map(remote.map((r) => [keyOf(r), r]));
  const localByKey = new Map(local.map((l) => [keyOf(l), l]));
  const holdKeys = options?.holdKeys;
  const actions: SyncAction[] = [];

  // --- ローカル基準 -------------------------------------------------------
  for (const l of local) {
    const key = keyOf(l);
    if (holdKeys?.has(key)) {
      // 未解決の競合。毎同期で「新規競合」扱いしない。
      actions.push({ type: 'in-sync', key: l });
      continue;
    }
    const r = remoteByKey.get(key);

    if (!r) {
      // リモートに無い。ローカルで作られたもの。
      // dirty でなくても、一度送ったものが消された可能性があるので送り直す。
      actions.push({ type: 'upload', local: l, expectedEtag: undefined });
      continue;
    }

    const localEtag = normalizeEtag(l.etag);
    const remoteEtag = normalizeEtag(r.etag);
    const sameVersion = localEtag !== undefined && localEtag === remoteEtag;

    if (!l.dirty) {
      // ローカルは変更していない
      if (sameVersion) actions.push({ type: 'in-sync', key: l });
      else actions.push({ type: 'download', remote: r, localRomId: l.romId });
      continue;
    }

    // ローカルは変更済み
    if (sameVersion) {
      // リモートは自分が最後に送った版のまま → そのまま上書きしてよい
      actions.push({ type: 'upload', local: l, expectedEtag: r.etag });
    } else if (!localEtag || l.updatedAt >= r.updatedAt) {
      // ETag 取りこぼし（localEtag 欠落）や、この端末の方が新しい変更なら
      // まず If-Match 付き upload を試す。412 なら apply 側で競合になる。
      // 終了直後に「必ず競合」になる主因（etag 不整合＋新規セーブ）をここで潰す。
      actions.push({ type: 'upload', local: l, expectedEtag: r.etag });
    } else {
      // リモートの方が新しく、ローカルも dirty → 触らず保留
      actions.push({ type: 'conflict', local: l, remote: r });
    }
  }

  // --- リモートにしかないもの ---------------------------------------------
  for (const r of remote) {
    const key = keyOf(r);
    if (localByKey.has(key)) continue;
    if (holdKeys?.has(key)) continue;

    const romId = romIdBySha1.get(r.sha1);
    if (!romId) {
      actions.push({ type: 'skip', remote: r, reason: 'rom-not-found' });
      continue;
    }
    actions.push({ type: 'download', remote: r, localRomId: romId });
  }

  return actions;
}

/** 競合の既定の推奨（UI の初期選択に使う）。新しい方を勧めるだけで、決定はユーザーに委ねる。 */
export function suggestResolution(local: LocalItem, remote: RemoteItem): 'local' | 'remote' {
  return local.updatedAt >= remote.updatedAt ? 'local' : 'remote';
}

/** Blob のパスから同期キーを復元する。サーバの一覧応答をパースするのに使う。 */
export function parseBlobPath(path: string): SyncKey | null {
  const m = /^([0-9a-f]{40})\/(sram\.bin|state-(-?\d{1,2})\.bin)$/.exec(path);
  if (!m) return null;
  const sha1 = m[1]!;
  if (m[2] === 'sram.bin') return { sha1, kind: 'sram' };
  return { sha1, kind: 'state', slot: Number(m[3]) };
}

/** 同期キーから Blob のパスを作る。 */
export function toBlobPath(k: SyncKey): string {
  return k.kind === 'sram' ? `${k.sha1}/sram.bin` : `${k.sha1}/state-${k.slot ?? 0}.bin`;
}
